import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconClose } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { daysBetween } from '../../../core/constants/dx';
import { useKpis } from '../../kpis/contexts/kpis';
import { PENDING_TYPES, flattenPending, pendingCounts, dueTone, todayStr } from '../../kpis/utils/pending';
import PendingCalendar from '../../kpis/layouts/pending-calendar';
import ClientFarmSearch from '../../clients/forms/search';
import PartoForm from '../../births/forms/parto-form';
import AbortForm from '../../births/forms/abort-form';
import SectionTabs from '../../navigation/section-tabs';
import { PLANNING_TABS } from '../../navigation/tabs';

import './styles/agenda.css';

// Previsión de tareas (admin-kpis.md ?view=pending). KPIs por tipo + mini calendario
// + board en columnas: OPU → sesión T.E. (finca) → tareas. Las OPU vienen COLAPSADAS
// por defecto y con acceso directo al Dx a nivel de OPU. Semáforo de vencimiento
// (rojo vencida · amarillo ≤2d · verde holgada). Filtra por cliente/finca.

function relLabel(dateStr) {
    if (!dateStr) return '';
    const d = daysBetween(todayStr(), dateStr);
    if (d == null) return '';
    if (d === 0) return 'hoy';
    if (d < 0) return `hace ${Math.abs(d)} d`;
    return `en ${d} d`;
}

const SECTIONS = [
    { key: 'dx1',    label: 'Diagnóstico 1', dateKey: 'scheduled_for', kind: 'dx' },
    { key: 'dx2',    label: 'Diagnóstico 2', dateKey: 'scheduled_for', kind: 'dx' },
    { key: 'births', label: 'Partos',        dateKey: 'expected',      kind: 'births' },
];
const STATUS_OPTS = [['all', 'Todo'], ['overdue', 'Vencidas'], ['pending', 'Pendientes']];
// Filtra un grupo pending por estado: overdue → solo vencidas · pending → solo próximas.
const filterGroup = (g = {}, st) => {
    if (st === 'overdue') return { overdue: g.overdue, upcoming: { items: [], count: 0 } };
    if (st === 'pending') return { overdue: { items: [], count: 0 }, upcoming: g.upcoming };
    return g;
};

function Chevron({ open }) {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
        </svg>
    );
}

const countTones = (tasks) => tasks.reduce((a, t) => { if (t._bucket === 'overdue') a.ov += 1; else a.up += 1; return a; }, { ov: 0, up: 0 });

// Une overdue+upcoming y agrupa OPU → T.E.(finca) → tareas. null si ningún item trae opu.
function buildTree(group, dateKey) {
    const tagged = [
        ...(group.overdue?.items || []).map((it) => ({ ...it, _bucket: 'overdue' })),
        ...(group.upcoming?.items || []).map((it) => ({ ...it, _bucket: 'upcoming' })),
    ];
    if (!tagged.some((it) => it.opu)) return null;

    const opuMap = new Map();
    for (const it of tagged) {
        const oKey = it.opu?.id ?? 'none';
        if (!opuMap.has(oKey)) opuMap.set(oKey, { opu: it.opu, teMap: new Map() });
        const o = opuMap.get(oKey);
        const tKey = it.te_session?.id ?? 'none';
        if (!o.teMap.has(tKey)) o.teMap.set(tKey, { te: it.te_session, farm: it.farm, tasks: [] });
        o.teMap.get(tKey).tasks.push(it);
    }
    const tasksSort = (a, b) => (a._bucket !== b._bucket ? (a._bucket === 'overdue' ? -1 : 1) : String(a[dateKey] || '').localeCompare(String(b[dateKey] || '')));
    return [...opuMap.values()]
        .sort((a, b) => (b.opu?.id ?? 0) - (a.opu?.id ?? 0))
        .map((o) => ({
            opu: o.opu,
            tes: [...o.teMap.values()]
                .sort((a, b) => String(b.te?.date || '').localeCompare(String(a.te?.date || '')))
                .map((te) => ({ te: te.te, farm: te.farm, tasks: te.tasks.sort(tasksSort) })),
        }));
}

// Fila de tarea (hoja). Color por urgencia (semáforo). Partos → botones Parto/Aborto.
function TaskRow({ task, dateKey, onParto, onAbort }) {
    const tone = dueTone(task[dateKey]);
    const label = `${task.recipient?.number || '—'}${task.recipient?.name ? ` · ${task.recipient.name}` : ''}`;
    return (
        <li className={`tasks-item tasks-item--${tone}`}>
            <span className="tasks-item__who" title={label}>{label}</span>
            <span className="tasks-item__when">
                {formatDateOnly(task[dateKey]) || '—'}
                <span className="tasks-item__rel">{relLabel(task[dateKey])}</span>
            </span>
            {(onParto || onAbort) && (
                <span className="tasks-item__actions">
                    <Gate domain="births" action="write">
                        <button type="button" className="tasks-item__btn tasks-item__btn--primary" onClick={() => onParto?.(task, label)}>Parto</button>
                    </Gate>
                    <Gate domain="medical_events" action="write">
                        <button type="button" className="tasks-item__btn tasks-item__btn--ghost" onClick={() => onAbort?.(task, label)}>Aborto</button>
                    </Gate>
                </span>
            )}
        </li>
    );
}

// Respaldo plano (sin agrupación por OPU): lista simple con semáforo.
function FlatList({ group, dateKey, onParto, onAbort }) {
    const items = [
        ...(group.overdue?.items || []),
        ...(group.upcoming?.items || []),
    ];
    const total = (group.overdue?.count || 0) + (group.upcoming?.count || 0);
    const extra = total - items.length;
    return (
        <div className="tasks-card__body">
            <ul className="tasks-list">
                {items.map((it) => <TaskRow key={it.diagnostic_id || it.pregnancy_id} task={it} dateKey={dateKey} onParto={onParto} onAbort={onAbort} />)}
            </ul>
            {extra > 0 && <span className="tasks-group__more">y {extra} más…</span>}
        </div>
    );
}

export default function TasksAgenda() {
    const [filters, setFilters] = useState({ client_id: '', farm_id: '' });
    const [active, setActive] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set()); // OPUs colapsadas por defecto
    const [parto, setParto] = useState(null);
    const [abort, setAbort] = useState(null);
    const [status, setStatus] = useState('all'); // all | overdue | pending
    const now = new Date();
    const [cal, setCal] = useState({ year: now.getFullYear(), month: now.getMonth() });

    const pick = (item, type) => {
        if (type === 'farm') {
            setFilters({ client_id: '', farm_id: String(item.id) });
            setActive({ type, label: `${item.name}${item.owner?.name ? ` · ${item.owner.name}` : ''}` });
        } else {
            setFilters({ client_id: String(item.id), farm_id: '' });
            setActive({ type, label: item.name });
        }
    };
    const clearFilter = () => { setFilters({ client_id: '', farm_id: '' }); setActive(null); };
    const toggle = (key) => setExpanded((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    const shiftMonth = (delta) => setCal((c) => { const d = new Date(c.year, c.month + delta, 1); return { year: d.getFullYear(), month: d.getMonth() }; });

    const openParto = (it, label) => setParto({ pregnancyId: it.pregnancy_id, recipientLabel: label });
    const openAbort = (it, label) => setAbort({ pregnancyId: it.pregnancy_id, recipientId: it.recipient?.id, recipientLabel: label });

    const { data, isLoading, isFetching, error } = useKpis('pending', filters);
    const pending = data?.pending || {};
    const counts = useMemo(() => pendingCounts(data?.pending || {}), [data]);
    const events = useMemo(() => flattenPending(data?.pending || {}), [data]);

    // OPUs presentes en cada etapa. Marca "parcial": una OPU cuyo cohorte ya avanzó de etapa
    // (aparece en una etapa posterior) → algunas ya se hicieron y faltan otras.
    const opuSets = useMemo(() => {
        const p = data?.pending || {};
        const mk = (key) => {
            const g = p[key] || {};
            const set = new Set();
            for (const it of [...(g.overdue?.items || []), ...(g.upcoming?.items || [])]) if (it.opu?.id != null) set.add(it.opu.id);
            return set;
        };
        return { dx1: mk('dx1'), dx2: mk('dx2'), births: mk('births') };
    }, [data]);
    const isParcial = (key, opuId) => opuId != null && (key === 'dx1' ? (opuSets.dx2.has(opuId) || opuSets.births.has(opuId)) : key === 'dx2' ? opuSets.births.has(opuId) : false);
    const shownEvents = useMemo(() => (status === 'overdue' ? events.filter((e) => e.bucket === 'overdue') : status === 'pending' ? events.filter((e) => e.bucket === 'upcoming') : events), [events, status]);

    return (
        <div className="tasks">
            <SectionTabs tabs={PLANNING_TABS} />
            <header className="tasks__header">
                <div className="tasks__heading">
                    <h1 className="tasks__title">Resumen</h1>
                    <p className="tasks__subtitle">Diagnósticos (dx1, dx2) y partos pendientes, agrupados por OPU.</p>
                </div>
                <div className="tasks__search">
                    {active ? (
                        <div className="tasks-active">
                            <span className="tasks-active__label">{active.type === 'farm' ? 'Finca' : 'Cliente'}</span>
                            <span className="tasks-active__value">{active.label}</span>
                            <button type="button" className="tasks-active__clear" onClick={clearFilter} aria-label="Quitar filtro"><IconClose /></button>
                        </div>
                    ) : (
                        <ClientFarmSearch scope="all" placeholder="Filtrar por cliente o finca…" onSelect={pick} />
                    )}
                </div>
            </header>

            {error && <div className="tasks__error">{error.message || 'No se pudo cargar la previsión de tareas'}</div>}

            {isLoading ? (
                <div className="tasks__state">Cargando previsión…</div>
            ) : (
                <>
                    <div className="tasks__overview">
                        <div className="tasks-kpis">
                            {SECTIONS.map((s) => {
                                const c = counts[s.key] || { total: 0, overdue: 0 };
                                return (
                                    <div key={s.key} className="tasks-kpi" style={{ '--type-color': PENDING_TYPES[s.key].color }}>
                                        <span className="tasks-kpi__value">{c.total}</span>
                                        <span className="tasks-kpi__label">{s.label}</span>
                                        {c.overdue > 0 && <span className="tasks-kpi__overdue">{c.overdue} vencida(s)</span>}
                                    </div>
                                );
                            })}
                            <div className="tasks-kpi tasks-kpi--total">
                                <span className="tasks-kpi__value">{counts.total}</span>
                                <span className="tasks-kpi__label">Total</span>
                                {counts.overdue > 0 && <span className="tasks-kpi__overdue">{counts.overdue} vencida(s)</span>}
                            </div>
                        </div>
                        <div className="tasks__calendar">
                            <PendingCalendar events={shownEvents} year={cal.year} month={cal.month} onMonthChange={shiftMonth} compact />
                        </div>
                    </div>

                    <div className="tasks-status" role="tablist" aria-label="Filtro de estado">
                        {STATUS_OPTS.map(([v, l]) => (
                            <button key={v} type="button" role="tab" aria-selected={status === v} className={`tasks-status__btn ${status === v ? 'tasks-status__btn--on' : ''}`} onClick={() => setStatus(v)}>{l}</button>
                        ))}
                    </div>

                    <div className={`tasks__board ${isFetching ? 'tasks__board--busy' : ''}`}>
                        {SECTIONS.map((s) => {
                            const group = filterGroup(pending[s.key] || {}, status);
                            const c = counts[s.key] || { overdue: 0, upcoming: 0 };
                            const empty = !(group.overdue?.count) && !(group.upcoming?.count);
                            const isBirths = s.kind === 'births';
                            const onP = isBirths ? openParto : undefined;
                            const onA = isBirths ? openAbort : undefined;
                            const tree = empty ? null : buildTree(group, s.dateKey);

                            return (
                                <section key={s.key} className="tasks-card" style={{ '--type-color': PENDING_TYPES[s.key].color }}>
                                    <header className="tasks-card__head">
                                        <h2 className="tasks-card__title">{s.label}</h2>
                                        <span className="tasks-card__counts">{c.overdue} vencida(s) · {c.upcoming} próxima(s)</span>
                                    </header>

                                    {empty ? (
                                        <p className="tasks-empty">Sin tareas.</p>
                                    ) : tree ? (
                                        <div className="tasks-card__tree">
                                            {tree.map((o) => {
                                                const oKey = `${s.key}|opu|${o.opu?.id ?? 'none'}`;
                                                const oOpen = expanded.has(oKey);
                                                const tasksAll = o.tes.flatMap((te) => te.tasks);
                                                const oc = countTones(tasksAll);
                                                const firstDate = tasksAll.map((t) => t[s.dateKey]).filter(Boolean).sort()[0];
                                                const parcial = isParcial(s.key, o.opu?.id);
                                                return (
                                                    <div key={oKey} className={`tasks-opu ${oc.ov > 0 ? 'tasks-opu--overdue' : ''}`}>
                                                        <div className="tasks-opu__head">
                                                            <button type="button" className="tasks-opu__toggle" onClick={() => toggle(oKey)} aria-expanded={oOpen}>
                                                                <Chevron open={oOpen} />
                                                                <span className="tasks-opu__title">{o.opu?.code || 'Sin OPU'}</span>
                                                                {firstDate && <span className="tasks-opu__date">{formatDateOnly(firstDate)}</span>}
                                                                <span className="tasks-opu__counts" title="vencidas / próximas">{oc.ov}/{oc.up}</span>
                                                                {oc.ov > 0 && <span className="tasks-opu__flag tasks-opu__flag--overdue" title="vencida">exp</span>}
                                                                {parcial && <span className="tasks-opu__flag tasks-opu__flag--parcial">parcial</span>}
                                                            </button>
                                                            {s.kind === 'dx' && o.opu?.id != null && (
                                                                <Link to={`/admin/dx?opu=${o.opu.id}`} className="tasks-opu__dx" title="Ir al diagnóstico de esta OPU">Dx →</Link>
                                                            )}
                                                        </div>
                                                        {oOpen && o.tes.map((te) => {
                                                            const tKey = `${oKey}|te|${te.te?.id ?? 'none'}`;
                                                            const tOpen = expanded.has(tKey);
                                                            const tc = countTones(te.tasks);
                                                            return (
                                                                <div key={tKey} className="tasks-te">
                                                                    <button type="button" className="tasks-te__head" onClick={() => toggle(tKey)} aria-expanded={tOpen}>
                                                                        <Chevron open={tOpen} />
                                                                        <span className="tasks-te__farm">{te.farm?.name || 'Sin finca'}</span>
                                                                        <span className="tasks-te__code">{te.te?.code || '—'}</span>
                                                                        <span className="tasks-te__counts" title="vencidas / próximas">{tc.ov}/{tc.up}</span>
                                                                    </button>
                                                                    {tOpen && (
                                                                        <ul className="tasks-list tasks-list--nested">
                                                                            {te.tasks.map((t) => (
                                                                                <TaskRow key={t.diagnostic_id || t.pregnancy_id} task={t} dateKey={s.dateKey} onParto={onP} onAbort={onA} />
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <FlatList group={group} dateKey={s.dateKey} onParto={onP} onAbort={onA} />
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </>
            )}

            {parto && <PartoForm pregnancyId={parto.pregnancyId} recipientLabel={parto.recipientLabel} onClose={() => setParto(null)} onSaved={() => setParto(null)} />}
            {abort && <AbortForm pregnancyId={abort.pregnancyId} recipientId={abort.recipientId} recipientLabel={abort.recipientLabel} onClose={() => setAbort(null)} onSaved={() => setAbort(null)} />}
        </div>
    );
}
